import React from 'react';
import styled from 'styled-components';
import { timeValueToDate2 } from '../../lib/commonFunc/commonFunc';
import { MdTrendingUp, MdDateRange, MdDirectionsRun, MdFitnessCenter, MdAccessTime } from 'react-icons/md';

const Container = styled.div`
  padding: 24px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background-color: #fff;
  margin-bottom: 20px;
  height: 260px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #ccc;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  height: 100%;
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Header = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h3`
  color: #333;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 5px;
`;

const DateItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateLabel = styled.span`
  color: #666;
  font-size: 14px;
  font-weight: 500;
`;

const DateValue = styled.span`
  color: #1976d2;
  font-size: 18px;
  font-weight: 700;
`;

const LastWorkoutSection = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 5px;
`;

const LastWorkoutTitle = styled.div`
  color: #666;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 48px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StatIcon = styled.div`
  color: #1976d2;
  font-size: 20px;
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  color: #333;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 2px;
`;

const StatLabel = styled.div`
  color: #888;
  font-size: 12px;
  font-weight: 500;
`;

const RightSection = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 100%;
`;

const DetailButton = styled.button`
  padding: 12px 24px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #1565c0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #666;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const RecordBoard = ({
  recordDates,
  record,
  target,
  onClick,
}) => {
  // 운동일 통계 계산
  const totalWorkoutDays = recordDates?.date ? recordDates.date.length : 0;
  const latestWorkoutDate = recordDates?.date ? recordDates.date[0] : null;
  
  // 최근 운동 데이터
  const recentWorkouts = record?.fcGroup?.fcGroupDetail ? record.fcGroup.fcGroupDetail.length : 0;
  const totalMinutes = record?.fcGroup?.fcGroupDetail ? 
    Math.floor(record.fcGroup.fcGroupDetail.reduce((total, fc) => total + fc.complete.time, 0) / 60) : 0;
  const totalCalories = record?.fcGroup?.fcGroupDetail ? 
    Math.floor(record.fcGroup.fcGroupDetail.reduce((total, fc) => total + fc.complete.calorie, 0) / 10) : 0;

  if (!target) {
    return (
      <Container>
        <ContentWrapper>
          <LeftSection>
            <Header>
              <Title>
                <MdDirectionsRun />
                운동 기록
              </Title>
            </Header>
            <ContentArea>
              <EmptyState style={{ padding: '20px 0', textAlign: 'left', height: 'auto' }}>
                <p>회원을 선택해 주세요</p>
              </EmptyState>
            </ContentArea>
          </LeftSection>
        </ContentWrapper>
      </Container>
    );
  }

  if (!recordDates || totalWorkoutDays === 0) {
    return (
      <Container>
        <ContentWrapper>
          <LeftSection>
            <Header>
              <Title>
                <MdDirectionsRun />
                운동 기록
              </Title>
              <DateInfo>
                <DateItem>
                  <DateLabel>총 운동일:</DateLabel>
                  <DateValue>0일</DateValue>
                </DateItem>
              </DateInfo>
            </Header>
            <ContentArea>
              <EmptyState style={{ padding: '20px 0', textAlign: 'left', height: 'auto' }}>
                <p>운동 기록이 없습니다</p>
              </EmptyState>
            </ContentArea>
          </LeftSection>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <LeftSection>
          <Header>
            <Title>
              <MdDirectionsRun />
              운동 기록
            </Title>
            <DateInfo>
              <DateItem>
                <DateLabel>총 운동일:</DateLabel>
                <DateValue>{totalWorkoutDays}일</DateValue>
              </DateItem>
            </DateInfo>
          </Header>
          
          <ContentArea>
            {latestWorkoutDate && (
              <LastWorkoutSection>
                <LastWorkoutTitle>
                  마지막 운동 ({timeValueToDate2(latestWorkoutDate)})
                </LastWorkoutTitle>
                <StatsContainer>
                  <StatItem>
                    <StatIcon>
                      <MdFitnessCenter />
                    </StatIcon>
                    <StatValue>{recentWorkouts}</StatValue>
                    <StatLabel>운동 수</StatLabel>
                  </StatItem>

                  <StatItem>
                    <StatIcon>
                      <MdAccessTime />
                    </StatIcon>
                    <StatValue>{totalMinutes}</StatValue>
                    <StatLabel>분</StatLabel>
                  </StatItem>

                  <StatItem>
                    <StatIcon>
                      <MdTrendingUp />
                    </StatIcon>
                    <StatValue>{totalCalories}</StatValue>
                    <StatLabel>칼로리</StatLabel>
                  </StatItem>
                </StatsContainer>
              </LastWorkoutSection>
            )}
          </ContentArea>
        </LeftSection>

        <RightSection>
          <DetailButton onClick={onClick}>
            운동기록 보기
          </DetailButton>
        </RightSection>
      </ContentWrapper>
    </Container>
  );
};

export default RecordBoard;